import {
    computed,
    defineComponent,
    ref,
} from "vue";
import {
    PublicFooter,
    PublicNavbar,
    usePublicLocalizedPath,
} from "./PublicShell.js";
import { useLocale } from "../../locales/i18n.js";
import { useVuePublicRouteData } from "@logscord/public-site/adapters/vue";
import PublicServerHistoryChart from "./PublicServerHistoryChart.js";
import {
    PublicServerAvatar,
    PublicServerBadgeList,
    PublicServerJoinModal,
    formatDirectoryCount,
    isPublicServerDirectoryItem,
    isServerJoinDisabled,
    openOrExplainServerJoin,
    type PublicServerDirectoryItem,
} from "./serverDirectoryShared.js";

export default defineComponent({
    setup() {
        const { i18n, locale } = useLocale();
        const publicRouteData = useVuePublicRouteData();
        const publicPath = usePublicLocalizedPath();
        const server = computed<PublicServerDirectoryItem | null>(() => {
            const value = publicRouteData.getData("publicServer");

            return isPublicServerDirectoryItem(value)
                ? value
                : null;
        });
        const joinModalServer = ref<PublicServerDirectoryItem | null>(null);
        const loading = computed(() => (
            publicRouteData.state.value?.status === "loading"
            || publicRouteData.state.value?.status === "idle"
        ));
        const failed = computed(() => (
            publicRouteData.state.value?.status === "unavailable"
            || publicRouteData.state.value?.status === "not-found"
        ));

        return () => (
            <div class="landing-page server-profile-page">
                <PublicNavbar />

                <main class="server-profile-main">
                    <router-link class="server-profile-back" to={publicPath("/servers")}>
                        <i class="material-symbols-outlined">arrow_back</i>
                        {i18n("server.back")}
                    </router-link>

                    {loading.value && <div class="server-profile-panel server-profile-skeleton" />}
                    {failed.value && <div class="server-directory-state">{i18n("server.not_found")}</div>}

                    {server.value && (
                        <>
                            <div class="server-profile-panel server-profile-hero">
                                <PublicServerAvatar server={server.value} />
                                <div class="server-profile-copy">
                                    <span class="landing-kicker">{i18n("server.kicker")}</span>
                                    <h1>{server.value.name}</h1>
                                    <p>{server.value.description || i18n("servers.card.no_description")}</p>
                                    <PublicServerBadgeList
                                        badges={server.value.badges}
                                        className="server-profile-badges"
                                    />
                                </div>
                                <button
                                    type="button"
                                    class={{
                                        "server-directory-join-button": true,
                                        "is-disabled": isServerJoinDisabled(server.value),
                                    }}
                                    onClick={() => openOrExplainServerJoin(server.value!, (selected) => {
                                        joinModalServer.value = selected;
                                    })}
                                >
                                    <i class="material-symbols-outlined">login</i>
                                    {i18n("servers.join.action")}
                                </button>
                            </div>

                            <div class="server-profile-metrics">
                                <article>
                                    <i class="material-symbols-outlined">groups</i>
                                    <strong>{formatDirectoryCount(server.value.memberCount, locale.value)}</strong>
                                    <span>{i18n("servers.metric.members")}</span>
                                </article>
                                <article>
                                    <i class="material-symbols-outlined">visibility</i>
                                    <strong>{formatDirectoryCount(server.value.approximateOnlineCount, locale.value)}</strong>
                                    <span>{i18n("servers.metric.online")}</span>
                                </article>
                                <article>
                                    <i class="material-symbols-outlined">settings_voice</i>
                                    <strong>{formatDirectoryCount(server.value.currentVoiceCount, locale.value)}</strong>
                                    <span>{i18n("servers.metric.voice")}</span>
                                </article>
                            </div>

                            <PublicServerHistoryChart
                                guildId={server.value.guildId}
                                locale={locale.value}
                                translate={i18n}
                            />
                        </>
                    )}
                </main>

                <PublicFooter />
                <PublicServerJoinModal
                    server={joinModalServer.value}
                    translate={i18n}
                    locale={locale.value}
                    onClose={() => {
                        joinModalServer.value = null;
                    }}
                />
            </div>
        );
    },
});
